import { WindowProxy } from '@sm360/widget-commons-core-services';

import { isArray, isFunction, isNumber, isObject, isString } from 'lodash';

/**
 * Service to push informations in the <dataLayer> variable of Google Tag Manager
 */
class AnalyticsService {
    /**
     * Verify if Google Tag Manager is configured in the website
     * @returns {boolean} is available or not
     */
    isAnalyticsAvailable() {
        return (
            !!WindowProxy.get() &&
            !!WindowProxy.get().dataLayer &&
            isArray(WindowProxy.get().dataLayer) &&
            isFunction(WindowProxy.get().dataLayer.push)
        );
    }

    /**
     * Push the event built from the given parameters to the <dataLayer>
     * @param {string} category The EventCategory
     * @param {string} action The EventAction
     * @param {string} label The EventLabel
     */
    eventDataLayer(category, action, label) {
        const areParamsValid = isString(category) && isString(action) && isString(label);
        if (this.isAnalyticsAvailable() && areParamsValid) {
            WindowProxy.get().dataLayer.push({
                category,
                action,
                label,
            });
        }
    }

    /**
     * Push the exception event built from the given parameters to the <dataLayer>
     * @param {string} exceptionSource
     * @param {string} label
     */
    exceptionDataLayer(exceptionSource, label) {
        this.eventDataLayer('Exception', exceptionSource, label);
    }

    /**
     * Push data into the <dataLayer> for a given pageType
     * @param {string} pageType i.e: inventory-detail
     * @param {object} object Data to push into the <dataLayer>
     */
    pushToDataLayer(pageType, object) {
        if (!this.isAnalyticsAvailable() || !isString(pageType) || !isObject(object)) {
            return;
        }

        WindowProxy.get().dataLayer.push({
            pageType,
            ...object,
        });
    }

    gaTrigger({ category, action, label }) {
        const areParamsValid = [category, action, label].every(isString);
        if (this.isAnalyticsAvailable() && areParamsValid) {
            WindowProxy.get().dataLayer.push({
                event: 'gaTriggerEvent',
                gaEventCategory: category,
                gaEventAction: action,
                gaEventLabel: label,
            });
        }
    }

    // New function to trigger ga4 with custom tags
    ga4Trigger({ category, label, ...customTags }) {
        const customTagsArray = Object.values(customTags);
        const paramsArray = [category, ...customTagsArray];
        if (label) paramsArray.push(label);

        const areParamsValid = paramsArray.every((value) => isString(value) || isNumber(value) || isArray(value));
        if (this.isAnalyticsAvailable() && areParamsValid) {
            const tagsObject = {
                event: 'gaTriggerEvent',
                gaEventCategory: category,
                gaEventLabel: label,
                ...customTags,
            };

            if (!label) delete tagsObject.gaEventLabel;

            WindowProxy.get().dataLayer.push(tagsObject);
        }
    }

    virtualPageView({ url, title, ...customTags }) {
        const areParamsValid = [url, title].every(isString);
        if (this.isAnalyticsAvailable() && areParamsValid) {
            WindowProxy.get().dataLayer.push({
                event: 'VirtualPageview',
                virtualPageURL: url,
                virtualPageTitle: title,
                ...customTags,
            });
        }
    }

    setDataLayerVariable(name, value) {
        if (this.isAnalyticsAvailable()) {
            WindowProxy.get().dataLayer.splice(0, 1, {
                ...WindowProxy.get().dataLayer[0],
                [name]: value,
            });
        }
    }

    pushDataLayerVariable(name, value) {
        if (this.isAnalyticsAvailable()) {
            WindowProxy.get().dataLayer.push({
                [name]: value,
            });
        }
    }
}

export default new AnalyticsService();
